package com.advaita.cloud.drive.shared

import kotlinx.serialization.Serializable

/*
 * ADV-1330 (P1.8): the share UX wire shapes — the share dialog, the people picker, and the
 * shared-with-me additions. DataHub fills every name and email from az2 for one response and
 * stores none of them. The vault holds ids alone.
 */

/** The three share levels (ruling S6), weakest first. A grant that matches none of them is [LEVEL_CUSTOM]. */
object ShareLevels {
    const val VIEWER = "viewer"
    const val EDITOR = "editor"
    const val COLLABORATOR = "collaborator"
    const val LEVEL_CUSTOM = "custom"
    val ALL: List<String> = listOf(VIEWER, EDITOR, COLLABORATOR)
}

/**
 * The state of one source behind a response: [OK], [UNAVAILABLE] (the source did not answer, so
 * the UI shows a "temporarily unavailable" note), or [REFUSED] (the source answered no for this
 * caller, for example az2 refuses a BaseSpace account). A source that is not [OK] adds nothing.
 *
 * On `/api/shared` `sharesStatus` alone: [REFUSED] means DataHub did not ask the vault, because
 * the session's org is not from the token's claim (`orgSource` is not `ORG_CLAIM`). [REJECTED]
 * means the vault answered the received read with a refusal, for example `403 org_conflict`.
 */
object SourceStatus {
    const val OK = "ok"
    const val UNAVAILABLE = "unavailable"
    const val REFUSED = "refused"
    const val REJECTED = "rejected"
}

/**
 * A person, a team or the org, by id. [name] and [email] come from az2 for people and from the
 * vault for teams. Both are null when the source did not name the id. A person's null name with
 * the people source [SourceStatus.OK] means az2 no longer lists that id in the org, for example a
 * disabled account (the UI shows "Former member").
 *
 * [id] is null for a person that az2 cannot name, on "Who has access" and as the sharer on
 * "Shared with me" (review SU-2), and the backend then sends no `id` key. The UI does not read
 * [id]. It removes or leaves a row by its grant id. Example: Kai left Acme. His row on Priya's
 * "Project X" reads "Former member" and carries no id, and Priya removes it through `grantId`.
 * Lena's Shared with me shows Kai's old share as shared by a former member, with no
 * `sharedBy.id`. With az2 down or refusing, no person is named, but the ids stay.
 */
@Serializable
data class PrincipalRef(
    val id: String? = null,
    val name: String? = null,
    val email: String? = null,
)

/** A grant that reaches the file through an ancestor folder: that folder. */
@Serializable
data class InheritedFromDto(
    val fileId: String,
    /** Null when the folder is not a live row in the caller's org. */
    val name: String? = null,
)

/**
 * One row of "Who has access". [level] is one of [ShareLevels.ALL], or [ShareLevels.LEVEL_CUSTOM].
 * [removable] and [relevelable] are the caller's standing, from the vault: with [relevelable]
 * false the UI disables the level control, and with [removable] false it hides the remove control
 * (S8: "Only Priya or the person who shared this can change it."). [self] marks the caller's own
 * USER grant, which the UI offers as "Leave".
 */
@Serializable
data class AccessGrantDto(
    val grantId: String,
    /** USER, TEAM or ORG. */
    val principalKind: String,
    val principal: PrincipalRef,
    val level: String,
    val capabilities: List<String>,
    val sharedBy: PrincipalRef,
    val createdAt: String,
    val expiresAt: String? = null,
    val inheritedFrom: InheritedFromDto? = null,
    val removable: Boolean,
    val relevelable: Boolean,
    val self: Boolean,
)

/**
 * `GET /api/files/{id}/access`. [levels] are the levels the caller may give, within what the caller holds.
 * A person that az2 cannot name ([owner], a grant's `principal` or `sharedBy`) has no id (see [PrincipalRef]).
 */
@Serializable
data class FileAccessResponse(
    val fileId: String,
    val owner: PrincipalRef,
    val levels: List<String>,
    val grants: List<AccessGrantDto>,
    val peopleStatus: String,
    val teamsStatus: String,
)

/** `POST /api/files/{id}/shares`. [principalKind] is USER or TEAM, and [level] one of [ShareLevels.ALL]. */
@Serializable
data class CreateShareRequest(
    val principalKind: String,
    val principalId: String,
    val level: String,
)

/** `PUT /api/shares/{grantId}`. */
@Serializable
data class SetShareLevelRequest(val level: String)

/** A grant as a write returns it. The UI reads the access list again for names and flags. */
@Serializable
data class ShareDto(
    val grantId: String,
    val principalKind: String,
    val principalId: String,
    val level: String,
    val capabilities: List<String>,
    val createdAt: String,
    val expiresAt: String? = null,
)

/**
 * One entry of the people picker. [kind] is USER or TEAM. For a USER, [signedIn] is false when the
 * vault has no directory row for the person ("Sam has not signed in to DataHub yet."), and null
 * when the vault could not say. [selectable] is false only for [signedIn] false. [memberCount] is a
 * team's alone.
 */
@Serializable
data class PickerItemDto(
    val kind: String,
    val id: String,
    val name: String? = null,
    val email: String? = null,
    val memberCount: Int? = null,
    val signedIn: Boolean? = null,
    val selectable: Boolean,
)

/** `GET /api/people` and `GET /api/people/recent`: people and teams in one list (S5). */
@Serializable
data class PickerResponse(
    val items: List<PickerItemDto>,
    val peopleStatus: String,
    val teamsStatus: String,
)

/**
 * ADV-1330 D5 (S7): how one shared-with-me root reaches the caller. When several grants reach
 * it, this is the caller's own USER grant if one exists, else the newest. [leavable] is true only
 * for the caller's own USER grant, which "Leave" removes with `DELETE /api/shares/{grantId}`.
 * A [sharedBy] that az2 cannot name has no id (review SU-2, see [PrincipalRef]).
 *
 * ADV-1330 (the level premise, Tim 2026-09-28): [effectiveCapabilities] is the sorted union of
 * the capabilities of every live grant of the caller on the root, so it can be wider than the
 * chosen grant's [capabilities]. The web enables its create buttons from it. Empty from an older
 * server, and the web then reads [capabilities]. Example: Kai shares Run 42 with Lena directly as
 * Viewer and with Sequencing Core as Editor. [level] is `viewer` (her own grant), and
 * [effectiveCapabilities] holds `upload` and `execute`.
 */
@Serializable
data class SharedGrantInfoDto(
    val grantId: String,
    val principalKind: String,
    val level: String,
    val capabilities: List<String>,
    val sharedBy: PrincipalRef,
    val createdAt: String,
    val leavable: Boolean,
    val effectiveCapabilities: List<String> = emptyList(),
    /** CR-6.4: when the chosen grant ends, or null for a grant with no end. Null from an older server. */
    val expiresAt: String? = null,
)

/**
 * CR-6 (Shared by me): who got a share. [kind] is USER, TEAM or ORG. [ref] names a USER or a TEAM
 * the way [PrincipalRef] does, and it is empty for ORG.
 */
@Serializable
data class GivenRecipientDto(
    val kind: String,
    val ref: PrincipalRef,
)

/**
 * CR-6: one live grant that the caller made, joined with its item. [itemType] is FILE or FOLDER.
 * [itemName] is null when the caller neither owns the item nor can read it, so the name stays
 * private. [itemParentPath] is the logical path of the folder that holds the item, and it is set
 * only when the caller owns the item: "Open" uses it. [removable] is the vault's answer for the
 * caller, and an absent answer counts as true, because the vault decides on the delete.
 */
@Serializable
data class GivenShareDto(
    val grantId: String,
    val itemId: String,
    val itemName: String? = null,
    val itemType: String,
    val itemParentPath: String? = null,
    val recipient: GivenRecipientDto,
    val level: String,
    val capabilities: List<String>,
    val createdAt: String,
    val expiresAt: String? = null,
    val removable: Boolean = true,
)

/** `GET /api/shares/given`, newest first. [peopleStatus] and [teamsStatus] are [SourceStatus] values. */
@Serializable
data class GivenSharesResponse(
    val shares: List<GivenShareDto>,
    val peopleStatus: String,
    val teamsStatus: String,
)
